import { appLocale } from './appLocale';

export type StatusBadgeColor = 'informative' | 'warning' | 'success' | 'danger' | 'subtle';

const CLOSED_STATUSES = ['filed', 'client_notified', 'cancelled'];

/** An open task whose due day has passed. The task list, the dashboard and the task page all use this one rule. */
export function isTaskOverdue(task: { status: string; dueDate: string }): boolean {
  return !CLOSED_STATUSES.includes(task.status) && daysUntil(task.dueDate) < 0;
}

export function statusColor(status: string): StatusBadgeColor {
  switch (status) {
    case 'pending': return 'informative';
    case 'in_progress': return 'warning';
    case 'filed':
    case 'client_notified': return 'success';
    case 'overdue': return 'danger';
    default: return 'subtle';
  }
}

/**
 * The calendar day of a WHOLE-DAY value: a due date, a period start or end, an effective date, a filed-on or debit
 * date. The fleet's BL-142 class: such a value is a day, not an instant. `new Date('2026-06-30')` is UTC midnight,
 * which renders as June 29 anywhere west of UTC ("Ended June 29" for Superlink's end of June 30, 2026-10-28). So
 * the day is read from the value's own yyyy-MM-dd and never converted. Null when the value has no such prefix.
 */
export function wholeDayParts(value?: string | null): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) } : null;
}

/** A whole-day value as a LOCAL midnight Date, for day arithmetic ("due in 3 days"). Null when unparsable. */
export function wholeDayLocalDate(value?: string | null): Date | null {
  const p = wholeDayParts(value);
  return p ? new Date(p.y, p.m - 1, p.d) : null;
}

/**
 * Formats a whole-day value as its own calendar day, in any time zone. Use it for every date that is a DAY; keep
 * `formatDate` for instants (a time something happened: filed at, sent at, synced at).
 */
export function formatWholeDay(
  value?: string | null,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: '2-digit' },
  locale?: string,
): string {
  if (!value) return '—';
  const p = wholeDayParts(value);
  if (!p) return value;
  return new Date(Date.UTC(p.y, p.m - 1, p.d)).toLocaleDateString(locale ?? appLocale(), { ...options, timeZone: 'UTC' });
}

/** An instant: when something happened. For a whole-day value use `formatWholeDay`. */
export function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(appLocale(), { year: 'numeric', month: 'short', day: '2-digit' });
}

export function formatCurrency(n?: number | null): string {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

/**
 * Whole days from today to a whole-day date (a due date): 0 on the day itself, negative once it has passed. Both
 * sides are calendar days, so a task due today is never "1 day overdue" from 00:01 on (BL-142: it used to subtract
 * instants, and a UTC-midnight due date was already behind "now" all day).
 */
export function daysUntil(iso: string): number {
  const due = wholeDayLocalDate(iso) ?? new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}
